import React from 'react';
import { Column, Options, Action } from 'material-table';
import { AxiosError, AxiosResponse } from 'axios';
import { Autocomplete } from '@material-ui/lab';
import { Button, Checkbox, createStyles, FormControlLabel, Grid, Icon, makeStyles, TextField } from '@material-ui/core';

import { getTableColumn, validateSpecialCharacter } from '../../../shared/operators';
import { TableLoading } from '../../../shared/components/TableLoading';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { UserContext } from '../UserContext';
import { TableCommon } from '../../../shared/components/TableCommon';
import { HomeContext } from '../../Home/HomeContext';
import { ProfileModel, profileModelInitial } from '../../../Models/ProfileModel';
import UserService from '../../../Services/UserService';
import ProfileService from '../../../Services/ProfileService';
import { UserModel } from '../../../Models/UserModel';
import { BasicPage } from '../../../shared/components/BasicPage';
import { SearchButtons } from '../../../shared/components/SearchButtons';

const useStyles = makeStyles(() => createStyles({
    button: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
    statusCheckbox: {
        alignSelf: 'center'
    }
}));

export function UserSearch() {
    const classes = useStyles();

    const userContext = React.useContext(UserContext);
    const homeContext = React.useContext(HomeContext);

    const [userName, setUserName] = React.useState<string>('');

    const [profileList, setProfileList] = React.useState<ProfileModel[]>([]);
    const [selectedProfile, setSelectedProfile] = React.useState<ProfileModel>(profileModelInitial);

    const [showAllStatus, setShowAllStatus] = React.useState<boolean>(true);
    
    const [hasSpecialCharacter, setHasSpecialCharacter] = React.useState(false);
    const [searchStatus, setSearchStatus] = React.useState<SearchStatus>(SearchStatus.INITIAL);

    React.useEffect(()=>{
        ProfileService.profileService().getAll()
            .then((res: AxiosResponse) => {
                if (res.data) {
                    setProfileList(res.data);
                }
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível carregar os tipos de perfis.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    }, []);

    const tableColumns: Column<UserModel>[] = [
        getTableColumn('Nome', 'username', 'string', 'left', { width: '30%'}),
        getTableColumn('Email', 'email', 'string', 'left', { width: '35%'}),
        getTableColumn('Perfil', 'profile.description', 'string', 'left', { width: '20%'}),
        {
            title: 'Ativo',
            render: rowData => (rowData.isActive ? 'Ativo' : 'Inativo'),
            field: 'isActive',
            type: 'string',
            align: 'left',
            cellStyle: { width: '15%'}
        },
    ];

    const tableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar usuário',
            onClick: (_, rowData) => {
                userContext.setUser(rowData);
                homeContext.setPageLocation(PageLocation.USER_EDIT);
            },
        }
    ];

    const tableOptions: Options<UserModel> = {
        filtering: true,
        toolbar: false,
        showTitle: false,
        actionsColumnIndex: -1,
        pageSizeOptions: [10, 50, 100, 200],
        pageSize: 50
    };
    
    const handleCleanFilter = () => {
        setUserName('');
        setSelectedProfile(profileModelInitial);
        setShowAllStatus(true);
    };

    const validateName = () => { setHasSpecialCharacter(validateSpecialCharacter(userName ?? '')); };

    const handleSearch = () => {
        if(!hasSpecialCharacter) {
            setSearchStatus(SearchStatus.LOADING);

            UserService.getUserByQuery(userName, selectedProfile.id, showAllStatus)
                .then((res: AxiosResponse)=> {
                    if(res.data){
                        userContext.setUserList(res.data);
                        if(res.data.length > 0)
                            setSearchStatus(SearchStatus.RESULT);
                        else
                            setSearchStatus(SearchStatus.EMPTY);
                    }
                }).catch((error: AxiosError) => {
                    userContext.setUserList([]);
                    setSearchStatus(SearchStatus.EMPTY);
                    homeContext.showError('Ops.. ocorreu um erro e não foi possível realizar a busca.' + 
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                });
        }
    };

    const getUserName = () => {
        return (
            <Grid item xs={12} sm={5}>
                <TextField
                    label="Nome do usuário"
                    variant="outlined"
                    fullWidth
                    value={userName}
                    onChange={(event) => setUserName(event.target.value) }
                    onBlur={validateName}
                    error={ hasSpecialCharacter }
                    helperText={ hasSpecialCharacter ? 'Caracteres especiais não são permitidos, altere a sua pesquisa.' : '' }
                    inputProps={{ maxLength: 100 }} />
            </Grid>
        );
    };

    const getProfile = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="profile-type"
                    value={selectedProfile}
                    onChange={(_, value: ProfileModel | null) => {
                        if (value)
                            setSelectedProfile(value);
                        else
                            setSelectedProfile(profileModelInitial);
                    }}
                    options={profileList}
                    getOptionLabel={(option: ProfileModel) => option.description}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField
                            {...params}
                            label="Perfil"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    const getStatus = () => {
        return (
            <Grid item xs={12} sm={3} className={classes.statusCheckbox}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showAllStatus}
                            onChange={ () => setShowAllStatus(!showAllStatus) }
                            name="Exibir usuários inativos"
                            color="primary" />
                    }
                    label="Exibir usuários inativos" />
            </Grid>
        );
    };

    const getAddNewButton = () => {
        return (
            <Grid item xs={12}>
                <Button
                    onClick={ () => homeContext.setPageLocation(PageLocation.USER_INPUT) }
                    size='large'
                    startIcon={<Icon color='primary'>add</Icon>}
                    className={classes.button}>
                    ADICIONAR NOVO USUÁRIO
                </Button>
            </Grid>
        );
    };

    return (
        <BasicPage
            title="Consulta de usuário"
            subtitle="Para visualizar a lista e consultar perfis de usuários cadastrados, insira um filtro de busca abaixo.">
            <>
                {getUserName()}
                {getProfile()}
                {getStatus()}

                <SearchButtons
                    searchStatus={searchStatus}
                    handleCleanFilter={handleCleanFilter}
                    handleSearch={handleSearch}/>

                <Grid item xs={12}>
                    {searchStatus == SearchStatus.INITIAL && 
                        <TableEmpty displayText='Os resultados de busca aparecerão aqui' />
                    } 
                    {searchStatus == SearchStatus.LOADING &&
                        <TableLoading displayText='Carregando usuários...' />
                    }
                    {searchStatus == SearchStatus.RESULT &&
                        <>
                            { getAddNewButton() }
                            <TableCommon 
                                tableData={userContext.userList}
                                tableColumns={tableColumns}
                                emptyDataSourceMessage='Nenhum usuário encontrado.'
                                tableActions={tableActions}
                                extraOptions={tableOptions} />
                        </>
                    }
                    {searchStatus == SearchStatus.EMPTY && 
                        <TableEmpty
                            displayText='Nenhum usuário foi encontrado' 
                            buttonText='ADICIONAR NOVO USUÁRIO'
                            pageLocation={PageLocation.USER_INPUT} />
                    }
                </Grid>
            </>
        </BasicPage>
    );
}